import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { CompanyLogo, UserAvatar } from "@/components/common";
import { ProjectStatusChip } from "@/components/canvas/Badges";
import { STATUS_LABEL, compactMoney, ago } from "@/lib/sk";

const ORDER = ["new", "draft", "configured", "calculated", "quoted", "sent", "negotiation", "won", "lost"];

function Th({ label, k, sort, setSort, className, align = "left" }) {
  const active = Boolean(k) && sort?.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cn("px-3 py-2.5 text-[12px] font-medium text-muted-foreground", align === "right" && "text-right", className)}>
      {k ? (
        <button type="button" onClick={() => setSort({ key: k, dir: active && sort.dir === "desc" ? "asc" : "desc" })} className={cn("group inline-flex items-center gap-1 rounded-md outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40", active && "text-foreground")} data-testid={`project-table-sort-${k}`}>
          {label}
          <Icon className={cn("h-3 w-3 transition-opacity", active ? "opacity-100" : "opacity-0 group-hover:opacity-60")} />
        </button>
      ) : label}
    </th>
  );
}

function Row({ p, testPrefix }) {
  return (
    <tr className="group" data-testid={`${testPrefix}-row-${p.number}`}>
      <td className="relative px-3 py-0">
        <Link to={`/p/${p.id}`} className="absolute inset-0 z-10 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40" aria-label={p.name} />
        <div className="flex min-w-0 items-center gap-3 py-3">
          <CompanyLogo name={p.customer?.name} domain={p.customer?.website} size="sm" className="h-9 w-9 rounded-[10px] p-1" />
          <div className="min-w-0">
            <div className="truncate text-[14px] font-medium text-foreground">{p.name || `Arbeidsområde #${p.number}`}</div>
            <div className="truncate text-[12.5px] text-muted-foreground">{p.customer?.name || "Ukjent kunde"}{p.vessel?.name ? ` · ${p.vessel.name}` : ""}<span className="ml-1.5 font-mono text-[11px] text-muted-foreground/60">#{p.number}</span></div>
          </div>
        </div>
      </td>
      <td className="hidden px-3 md:table-cell"><ProjectStatusChip status={p.status} /></td>
      <td className="hidden px-3 text-right font-mono text-[13px] tabular-nums lg:table-cell">{p.items_count ? p.items_count : <span className="text-muted-foreground/40">–</span>}</td>
      <td className="px-3 text-right font-mono text-[13px] tabular-nums text-foreground/85">{p.value ? compactMoney(p.value) : <span className="text-muted-foreground/40">–</span>}</td>
      <td className="hidden px-3 sm:table-cell"><div className="flex justify-center"><UserAvatar name={p.owner?.name} initials={p.owner?.initials} color={p.owner?.color} src={p.owner?.avatar_url} size="md" className="h-7 w-7" /></div></td>
      <td className="hidden px-3 text-right text-[12px] text-muted-foreground md:table-cell">
        <span className="inline-flex items-center gap-1.5">{p.clarification_pending ? <Clock className="h-3 w-3 text-[hsl(var(--v-assumed))]" title="Venter på kunde" /> : null}{ago(p.updated_at)}</span>
      </td>
      <td className="w-8 pr-3 text-right"><ChevronRight className="inline h-4 w-4 -translate-x-1 text-muted-foreground/60 opacity-0 transition-[transform,opacity] group-hover:translate-x-0 group-hover:opacity-100" /></td>
    </tr>
  );
}

/** One table for projects everywhere: sortable, optional grouping by status, row = link to the canvas. */
export function ProjectTable({ rows, groupByStatus = false, emptyText = "Ingen arbeidsområder matcher.", testPrefix = "projects", className }) {
  const [sort, setSort] = useState({ key: "updated_at", dir: "desc" });
  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (p) => (sort.key === "name" ? (p.name || "").toLowerCase() : sort.key === "customer" ? (p.customer?.name || "").toLowerCase() : sort.key === "value" ? p.value || 0 : sort.key === "items_count" ? p.items_count || 0 : p.updated_at || "");
    return [...rows].sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
  }, [rows, sort]);
  const groups = useMemo(() => {
    if (!groupByStatus) return [[null, sorted]];
    const m = new Map();
    sorted.forEach((p) => m.set(p.status, [...(m.get(p.status) || []), p]));
    return ORDER.filter((s) => m.has(s)).map((s) => [s, m.get(s)]);
  }, [sorted, groupByStatus]);

  return (
    <div className={cn("overflow-hidden rounded-[18px] border border-black/[0.06] bg-white", className)} data-testid={`${testPrefix}-table`}>
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col />
          <col className="hidden w-[130px] md:table-column" />
          <col className="hidden w-[90px] lg:table-column" />
          <col className="w-[110px]" />
          <col className="hidden w-[64px] sm:table-column" />
          <col className="hidden w-[120px] md:table-column" />
          <col className="w-8" />
        </colgroup>
        <thead className="border-b border-black/[0.06]">
          <tr>
            <Th label="Prosjekt" k="name" sort={sort} setSort={setSort} />
            <Th label="Status" className="hidden md:table-cell" />
            <Th label="Poster" k="items_count" sort={sort} setSort={setSort} align="right" className="hidden lg:table-cell" />
            <Th label="Verdi" k="value" sort={sort} setSort={setSort} align="right" />
            <Th label="Eier" className="hidden text-center sm:table-cell" />
            <Th label="Oppdatert" k="updated_at" sort={sort} setSort={setSort} align="right" className="hidden md:table-cell" />
            <th />
          </tr>
        </thead>
        {rows.length ? groups.map(([status, list]) => (
          <tbody key={status || "all"} className="[&_tr:hover]:bg-[hsl(210_20%_98%)] [&_tr+tr]:border-t [&_tr+tr]:border-black/[0.045]">
            {status ? (
              <tr className="!bg-[hsl(210_20%_98.6%)]" data-testid={`${testPrefix}-group-${status}`}>
                <td colSpan={7} className="px-3 py-2 text-[12px] font-semibold text-foreground/80">
                  {STATUS_LABEL[status]} <span className="ml-1 font-mono text-[11px] font-medium text-muted-foreground">{list.length}</span>
                  <span className="ml-3 font-mono text-[11px] font-medium text-muted-foreground">{compactMoney(list.reduce((s, p) => s + (p.value || 0), 0))}</span>
                </td>
              </tr>
            ) : null}
            {list.map((p) => <Row key={p.id} p={p} testPrefix={testPrefix} />)}
          </tbody>
        )) : (
          <tbody><tr><td colSpan={7} className="px-5 py-12 text-center text-[13px] text-muted-foreground">{emptyText}</td></tr></tbody>
        )}
      </table>
    </div>
  );
}
